import { useMemo, useState } from 'react'
import { View, Text, TextInput as RNTextInput, Platform } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import { MessageCircle, Search } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Avatar } from '@/components/ui/Avatar'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { useConversations } from '@/lib/hooks/use-conversations'
import { useSemanticColors, SPACING, RADIUS, TEXT_STYLES } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useFontScale } from '@/lib/hooks/use-font-scale'
import { PageShell } from '@/components/layout/PageShell'
import {
  bookingDateKey,
  formatBookingDate,
  formatBookingDateTime,
  todayInBookingTimeZoneISO,
} from '@/lib/utils/dates'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

/** Whole calendar days between two `YYYY-MM-DD` keys. */
function daysBetweenDayKeys(fromKey: string, toKey: string): number {
  const from = Date.parse(`${fromKey}T00:00:00Z`)
  const to = Date.parse(`${toKey}T00:00:00Z`)
  if (Number.isNaN(from) || Number.isNaN(to)) return 0
  return Math.round((to - from) / ONE_DAY_MS)
}

/**
 * Calendar days pinned to America/El_Salvador (lib/utils/dates.ts), not
 * elapsed milliseconds in the device timezone. Elapsed-ms said "10:40 p. m."
 * for a message sent at 11 p.m. the night before, while the chat screen put a
 * "Ayer" separator above that very message.
 */
function formatTimestamp(iso: string | null, t: (key: string) => string, locale: string): string {
  if (!iso) return ''
  const key = bookingDateKey(iso)
  if (!key) return ''

  const days = daysBetweenDayKeys(key, todayInBookingTimeZoneISO())
  if (days <= 0) {
    return formatBookingDateTime(iso, locale, {
      day: undefined,
      month: undefined,
      year: undefined,
    })
  }
  if (days === 1) return t('messages.yesterday')
  if (days < 7) {
    return formatBookingDate(iso, locale, {
      weekday: 'short',
      day: undefined,
      month: undefined,
      year: undefined,
    })
  }
  return formatBookingDate(iso, locale, { month: 'short', day: 'numeric', year: undefined })
}

export default function MessagesScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { isDesktop: isWide } = useBreakpoint()
  // The avatar is a fixed 40dp disc; the name/preview/timestamp block beside it
  // roughly doubles in height at 2.0. Centring the two against each other then
  // parks the disc level with the message preview while the name it belongs to
  // sits a line and a half above it, so the row reads as two unrelated things.
  const { enlarged } = useFontScale()
  const { conversations, isLoading, isRefetching, error, refetch } = useConversations()
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim().toLowerCase()
  const filteredConversations = useMemo(() => {
    if (!normalizedQuery) return conversations
    return conversations.filter((conv) => {
      const name = conv.otherUserDisplayName?.toLowerCase() ?? ''
      const preview = conv.lastMessagePreview?.toLowerCase() ?? ''
      return name.includes(normalizedQuery) || preview.includes(normalizedQuery)
    })
  }, [conversations, normalizedQuery])

  return (
    <ScreenContainer variant={isWide ? 'dashboard' : 'reading'} scroll={false}>
      <PageShell title={t('messages.title')}>
        {/* Mobile-only title row — desktop title lives in PageShell.
            No `insets.top` here: ScreenContainer already pads the safe
            area, and adding it twice left a status-bar-tall dead band
            above the title. */}
        {!isWide && (
          <View
            style={{
              paddingHorizontal: SPACING.lg,
              paddingTop: SPACING.sm,
              paddingBottom: SPACING.md,
            }}
          >
            <Text
              style={{
                ...TEXT_STYLES.h1,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('messages.title')}
            </Text>
          </View>
        )}

        <View
          style={
            isWide ? { flex: 1, flexDirection: 'row', gap: SPACING.lg, minHeight: 0 } : { flex: 1 }
          }
        >
          <View
            style={
              isWide
                ? {
                    width: 420,
                    minWidth: 360,
                    flexShrink: 0,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderRadius: RADIUS.xl,
                    backgroundColor: colors.surface,
                    overflow: 'hidden',
                  }
                : { flex: 1 }
            }
          >
            <View
              style={{
                paddingHorizontal: isWide ? SPACING.md : SPACING.lg,
                paddingTop: isWide ? SPACING.md : 0,
                paddingBottom: SPACING.md,
              }}
            >
              <GlassSurface
                tone="quiet"
                radius={RADIUS.full}
                style={{
                  minHeight: 46,
                }}
                contentStyle={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.sm,
                  paddingHorizontal: SPACING.md,
                }}
              >
                <Search size={18} color={colors.textMuted} strokeWidth={2} />
                <RNTextInput
                  value={query}
                  onChangeText={setQuery}
                  placeholder={t('messages.search_placeholder')}
                  placeholderTextColor={colors.textMuted}
                  autoCapitalize="none"
                  autoCorrect={false}
                  returnKeyType="search"
                  // A placeholder is not a label: it disappears the moment
                  // anything is typed, and the audit read this input as having
                  // no accessible name at all.
                  accessibilityLabel={t('messages.search_placeholder')}
                  style={[
                    {
                      flex: 1,
                      alignSelf: 'stretch',
                      fontSize: 15,
                      lineHeight: 20,
                      fontFamily: FONTS.body,
                      color: colors.textPrimary,
                      paddingVertical: SPACING.sm + SPACING.xs,
                    },
                    Platform.OS === 'web'
                      ? ({ outlineStyle: 'none' } as Record<string, string>)
                      : null,
                  ]}
                  testID="messages.search-input"
                />
              </GlassSurface>
            </View>

            <ScrollView
              contentContainerStyle={{
                paddingBottom: isWide ? SPACING.md : insets.bottom + SPACING.xl,
              }}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
              refreshControl={
                <BrandedRefreshControl refreshing={isRefetching} onRefresh={() => void refetch()} />
              }
            >
              {isLoading ? (
                <View
                  style={{ paddingHorizontal: isWide ? SPACING.md : SPACING.lg, gap: SPACING.md }}
                >
                  {[0, 1, 2].map((i) => (
                    <View
                      key={i}
                      style={{ flexDirection: 'row', gap: SPACING.md, alignItems: 'center' }}
                    >
                      <Skeleton width={48} height={48} borderRadius={24} />
                      <View style={{ flex: 1, gap: SPACING.xs + 2 }}>
                        <Skeleton width="60%" height={16} borderRadius={RADIUS.xs} />
                        <Skeleton width="80%" height={14} borderRadius={RADIUS.xs} />
                      </View>
                    </View>
                  ))}
                </View>
              ) : error && conversations.length === 0 ? (
                <View
                  style={{ paddingTop: SPACING['3xl'] - SPACING.xs, paddingHorizontal: SPACING.lg }}
                >
                  <EmptyState.OfflineError onRetry={() => void refetch()} />
                </View>
              ) : conversations.length === 0 ? (
                <View
                  style={{ paddingTop: SPACING['3xl'] - SPACING.xs, paddingHorizontal: SPACING.lg }}
                >
                  {/* Trips, listings, payouts and payment-methods all close
                      their empty state on a button. This one stopped at the
                      grey sentence, so the block ended mid-thought with the
                      rest of the screen reading as a gap rather than as
                      breathing room. A conversation starts from a car. */}
                  <EmptyState
                    variant="bloom"
                    illustration={
                      <MessageCircle size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />
                    }
                    title={t('messages.no_conversations')}
                    description={t('messages.no_conversations_desc')}
                    action={{
                      label: t('explore.search_cta'),
                      onPress: () => router.push('/(tabs)/explore'),
                    }}
                  />
                </View>
              ) : filteredConversations.length === 0 ? (
                <View
                  style={{ paddingTop: SPACING['3xl'] - SPACING.xs, paddingHorizontal: SPACING.lg }}
                >
                  <EmptyState
                    compact
                    variant="minimal"
                    illustration={<Search size={34} color={colors.textMuted} strokeWidth={1.5} />}
                    title={t('messages.no_results')}
                    description={t('messages.no_results_desc')}
                  />
                </View>
              ) : (
                <View testID="messages.conversation-list">
                  {filteredConversations.map((conv, index) => {
                    const hasUnread = conv.unreadCount > 0
                    return (
                      <Pressable
                        key={conv.id}
                        onPress={() => router.push(`/messages/${conv.id}`)}
                        accessibilityRole="button"
                        accessibilityLabel={
                          conv.otherUserDisplayName ?? t('messages.new_conversation')
                        }
                        testID={`messages.conversation-${index}`}
                        style={({ pressed }) => ({
                          flexDirection: 'row',
                          // Top-aligned once the text block is tall enough for
                          // the difference to show: the disc belongs to the
                          // NAME, so it hangs from the same line the name
                          // starts on rather than floating at the row's
                          // midpoint. At scale 1 the two heights are close
                          // enough that 'center' is still what renders.
                          alignItems: enlarged ? 'flex-start' : 'center',
                          paddingHorizontal: isWide ? SPACING.md : SPACING.lg,
                          paddingVertical: SPACING.md - 1,
                          gap: SPACING.md,
                          opacity: pressed ? 0.76 : 1,
                        })}
                      >
                        <View style={{ position: 'relative' }}>
                          <Avatar
                            size="md"
                            source={conv.otherUserAvatarUrl}
                            fallback={conv.otherUserDisplayName ?? '?'}
                            accessibilityLabel={conv.otherUserDisplayName ?? ''}
                          />
                          {hasUnread && (
                            <View
                              style={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                minWidth: 18,
                                height: 18,
                                borderRadius: 9,
                                backgroundColor: COLORS.coral.dark,
                                borderWidth: 2,
                                borderColor: colors.background,
                                alignItems: 'center',
                                justifyContent: 'center',
                                paddingHorizontal: SPACING.xs,
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontFamily: FONTS.bodyBold,
                                  color: '#FFFFFF',
                                }}
                              >
                                {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                              </Text>
                            </View>
                          )}
                        </View>
                        <View
                          style={{
                            flex: 1,
                            borderBottomWidth: 0.5,
                            borderBottomColor: colors.border,
                            paddingBottom: SPACING.md - 1,
                          }}
                        >
                          {/* Name and timestamp share a line only while both
                              fit it. A Salvadoran full name at 2.0 needs the
                              whole measure on its own, so past the baseline the
                              timestamp drops below the name instead of eating
                              into it — "David Isaac Gutierrez T…" was the name
                              being cut so a relative time could keep its
                              corner. */}
                          <View
                            style={{
                              flexDirection: enlarged ? 'column' : 'row',
                              justifyContent: 'space-between',
                              alignItems: enlarged ? 'flex-start' : 'center',
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 15,
                                fontFamily: hasUnread ? FONTS.bodyBold : FONTS.body,
                                color: colors.textPrimary,
                                flex: enlarged ? undefined : 1,
                                alignSelf: enlarged ? 'stretch' : undefined,
                                marginRight: enlarged ? 0 : SPACING.sm,
                              }}
                              numberOfLines={enlarged ? 2 : 1}
                            >
                              {conv.otherUserDisplayName ?? t('messages.new_conversation')}
                            </Text>
                            <Text
                              style={{
                                fontSize: 12,
                                fontFamily: FONTS.body,
                                color: hasUnread ? colors.textAccent : colors.textMuted,
                                marginTop: enlarged ? SPACING.xs : 0,
                              }}
                            >
                              {formatTimestamp(conv.lastMessageAt, t, i18n.language)}
                            </Text>
                          </View>
                          <Text
                            style={{
                              fontSize: 14,
                              fontFamily: FONTS.body,
                              color: hasUnread ? colors.textPrimary : colors.textMuted,
                              marginTop: 3,
                            }}
                            // The preview is a preview at any size — one line
                            // is the design. Two at large text so the sentence
                            // has a chance to say something.
                            numberOfLines={enlarged ? 2 : 1}
                          >
                            {conv.lastMessagePreview ?? ''}
                          </Text>
                        </View>
                      </Pressable>
                    )
                  })}
                </View>
              )}
            </ScrollView>
          </View>

          {isWide && (
            <View
              style={{
                flex: 1,
                minWidth: 0,
                borderWidth: 1,
                borderColor: colors.border,
                borderRadius: RADIUS.xl,
                backgroundColor: colors.surface,
                alignItems: 'center',
                justifyContent: 'center',
                padding: SPACING['2xl'],
              }}
            >
              <View
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 30,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${COLORS.coral.DEFAULT}10`,
                  marginBottom: SPACING.md,
                }}
              >
                <MessageCircle size={30} color={COLORS.coral.DEFAULT} strokeWidth={1.7} />
              </View>
              <Text
                style={{
                  ...TEXT_STYLES.h2,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  textAlign: 'center',
                }}
              >
                {t('messages.desktop_empty_title')}
              </Text>
              <Text
                style={{
                  marginTop: SPACING.xs,
                  fontSize: 15,
                  lineHeight: 22,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  maxWidth: 360,
                }}
              >
                {t('messages.desktop_empty_desc')}
              </Text>
            </View>
          )}
        </View>
      </PageShell>
    </ScreenContainer>
  )
}
